import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import mermaid from "mermaid";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import type { ArtifactLineageGraph } from "~/components/assistant/types";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

interface LineageTabProps {
  artifactId: string;
}

export function LineageTab({ artifactId }: LineageTabProps) {
  const { api, conversationId, contextId } = useAssistantContext();
  const [lineageData, setLineageData] = useState<ArtifactLineageGraph | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasRendered = useRef(false);
  const hasTrackedView = useRef(false);

  // Initialize mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
    });
  }, []);

  // Fetch lineage data when component mounts
  useEffect(() => {
    let cancelled = false;

    const fetchLineage = async () => {
      if (!conversationId || !artifactId) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await api.getArtifactLineage(conversationId, artifactId);
        if (!cancelled) {
          setLineageData(data);

          // Track successful lineage view (only once per artifact)
          if (contextId && !hasTrackedView.current) {
            hasTrackedView.current = true;
            trackEvent("analysis_agent_artifact_lineage_viewed", {
              dataset_id: contextId,
              conversation_id: conversationId || undefined,
              artifact_id: artifactId,
              context: "dataset",
              node_count: data?.nodes?.length || 0,
              edge_count: data?.edges?.length || 0,
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load lineage");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchLineage();

    return () => {
      cancelled = true;
    };
  }, [api, conversationId, artifactId]);

  // Render mermaid diagram when data is available
  useEffect(() => {
    if (!lineageData || !containerRef.current || hasRendered.current) {
      return;
    }

    const renderDiagram = async () => {
      if (!lineageData.mermaid || !containerRef.current) {
        return;
      }

      try {
        // Clear container
        containerRef.current.innerHTML = "";

        // Check if there are any nodes to display
        if (!lineageData.nodes || lineageData.nodes.length === 0) {
          containerRef.current.innerHTML =
            '<p class="text-xs text-gray-600">No lineage found for this artifact.</p>';
          hasRendered.current = true;
          return;
        }

        // Generate unique ID for this diagram
        const diagramId = `lineage-${artifactId}-${Date.now()}`;

        // Render the mermaid diagram
        const { svg } = await mermaid.render(diagramId, lineageData.mermaid);

        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          hasRendered.current = true;
        }
      } catch (err) {
        console.error("Failed to render mermaid diagram:", err);
        if (containerRef.current) {
          containerRef.current.innerHTML = `<p class="text-xs text-red-600">Failed to render diagram: ${
            err instanceof Error ? err.message : "Unknown error"
          }</p>`;
        }
      }
    };

    renderDiagram();
  }, [lineageData, artifactId]);

  return (
    <div className="py-4">
      <div className="text-xs text-gray-600 mb-4">
        <p>
          This diagram shows how this artifact was created, including all parent artifacts and
          source tables that contributed to it.
        </p>
      </div>
      {isLoading ? (
        <div
          className={cn(
            "overflow-x-auto bg-white rounded-sm border border-gray-200 p-4",
            "flex items-center justify-center py-8"
          )}
        >
          <Loader2 className="size-6 text-gray-400 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : !lineageData ? (
        <p className="text-xs text-gray-600">No lineage data available.</p>
      ) : (
        <div
          ref={containerRef}
          className={cn(
            "overflow-x-auto bg-white rounded-sm border border-gray-200 p-4",
            "flex items-center justify-center"
          )}
        />
      )}
    </div>
  );
}
